import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { PAGE_SIZE } from 'src/api/wf/constants';
import {
  WfOrganizationSalesOfficeLocation,
  WfStatusOptionsGroup,
} from '../repository/WfOptionsRepo';
import { WfOperationStatus } from '../models/enums/WfOperationtStatus';
import { sharedStorage } from 'src/common/storage/storage';

export interface WfBranchOption {
  code: string;
  name: string;
}

export interface WfSalesOfficeOption extends WfBranchOption {
  location?: WfOrganizationSalesOfficeLocation;
  defaultScale?: number;
  searchRange?: number;
}

export interface WfSectionOption extends WfBranchOption {
  location?: WfOrganizationSalesOfficeLocation;
  defaultScale?: number;
  searchRange?: number;
}

export type WfSquadOption = WfBranchOption;

export enum WfReservedOptions {
  all = '0',
  unreserved = '1',
  reserved = '2',
}

export interface WfFilterParameters {
  branch?: string;
  salesOffice?: string;
  section?: string;
  squad?: string;
  statusList: WfOperationStatus;
  customerNumber?: string;
  reserved?: WfReservedOptions;
  disasterId?: string;
}

export interface WfFilterPagingParameters {
  pageNumber: number;
  pageSize: number;
}

export interface WfAlertFilterParameters {
  branchCode: string;
  salesOfficeCode: string;
  sectionCode: string;
  squadCode: string;
  customerNumber?: string;
}

type MapLocation = {
  latitude: number;
  longitude: number;
};

type WfFilterParametersPatch = {
  [K in keyof WfFilterParameters]?: WfFilterParameters[K] | undefined;
};

interface WfFilterStore {
  filterParameters: WfFilterParameters;
  filterPagingParameters: WfFilterPagingParameters;
  alertFilterParameters: WfAlertFilterParameters;
  branchOptions: WfBranchOption[];
  salesOfficeOptions: WfSalesOfficeOption[];
  sectionOptions: WfSectionOption[];
  squadOptions: WfSquadOption[];
  reserved: WfReservedOptions;
  statusOptions: WfStatusOptionsGroup[];
  mapCenter: MapLocation | null;
  zoom: number | null;
  userOffice: WfSalesOfficeOption | null;
  filtersInitialized: boolean;
  patchFilterParameters: (filterParameters: WfFilterParametersPatch) => void;
  patchFilterPagingParameters: (patch: { pageNumber?: number; pageSize?: number }) => void;
  patchAlertFilterParameters: (patch: Partial<WfAlertFilterParameters>) => void;
  setBranchOptions: (options: WfBranchOption[]) => void;
  setSalesOfficeOptions: (options: WfSalesOfficeOption[]) => void;
  setSectionOptions: (options: WfSectionOption[]) => void;
  setSquadOptions: (options: WfSquadOption[]) => void;
  setReserved: (reserved: WfReservedOptions) => void;
  setStatusOptions: (options: WfStatusOptionsGroup[]) => void;
  setMapCenter: (location: MapLocation | null) => void;
  setUserOffice: (office: WfSalesOfficeOption | null) => void;
  setZoom: (zoom: number | null) => void;
  setFiltersInitialized: (initialized: boolean) => void;
  resetFilters: () => void;
}

const FILTER_STORE_STORAGE_KEY = 'wf-filter-store';

const createDefaultFilterData = () => ({
  filterParameters: {
    branch: '',
    salesOffice: '',
    section: '',
    squad: '',
    statusList: WfOperationStatus.Incomplete,
    customerNumber: '',
    reserved: WfReservedOptions.unreserved,
  },
  filterPagingParameters: {
    pageNumber: 0,
    pageSize: PAGE_SIZE,
  },
  alertFilterParameters: {
    branchCode: '',
    salesOfficeCode: '',
    sectionCode: '',
    squadCode: '',
    customerNumber: '',
  },
  branchOptions: [],
  salesOfficeOptions: [],
  sectionOptions: [],
  squadOptions: [],
  reserved: WfReservedOptions.all,
  statusOptions: [] as WfStatusOptionsGroup[],
  mapCenter: null,
  userOffice: null,
  zoom: null,
  filtersInitialized: false,
});

export const useWfFilterStore = create<WfFilterStore>()(
  persist(
    (set) => ({
      ...createDefaultFilterData(),
      patchFilterParameters: (filterParameters: WfFilterParametersPatch) =>
        set((state) => {
          const newFilterParameters: WfFilterParameters = {
            ...state.filterParameters,
            ...filterParameters,
          } as WfFilterParameters;
          return {
            filterParameters: newFilterParameters,
          };
        }),
      patchFilterPagingParameters: (patch) =>
        set((state) => {
          const newFilterPagingParameters = {
            ...state.filterPagingParameters,
            ...(patch.pageNumber != null && { pageNumber: patch.pageNumber }),
            ...(patch.pageSize != null && { pageSize: patch.pageSize }),
          };
          return {
            filterPagingParameters: newFilterPagingParameters,
          };
        }),
      patchAlertFilterParameters: (patch: Partial<WfAlertFilterParameters>) =>
        set((state) => ({
          alertFilterParameters: { ...state.alertFilterParameters, ...patch },
        })),
      setMapCenter: (location: MapLocation | null) =>
        set({
          mapCenter: location
            ? { latitude: location.latitude, longitude: location.longitude }
            : null,
        }),
      setUserOffice: (userOffice) => set({ userOffice }),
      setBranchOptions: (options) => set({ branchOptions: options }),
      setSalesOfficeOptions: (options) => set({ salesOfficeOptions: options }),
      setSectionOptions: (options) => set({ sectionOptions: options }),
      setSquadOptions: (options) => set({ squadOptions: options }),
      setReserved: (reserved: WfReservedOptions) => set({ reserved }),
      setStatusOptions: (options) => set({ statusOptions: options }),
      setZoom: (zoom) => set({ zoom }),
      setFiltersInitialized: (initialized: boolean) => set({ filtersInitialized: initialized }),
      resetFilters: () =>
        set({
          ...createDefaultFilterData(),
        }),
    }),
    {
      name: FILTER_STORE_STORAGE_KEY,
      storage: createJSONStorage(() => sharedStorage),
      partialize: (state) => ({
        filterParameters: state.filterParameters,
        filterPagingParameters: state.filterPagingParameters,
        filtersInitialized: state.filtersInitialized,
      }),
    }
  )
);
